
import React, { useState, useMemo } from 'react';
import { Order, PaymentRecord, PaymentStatus } from '../types';
import { Search, DollarSign, Calendar, Edit2, Check, X, ArrowUpDown, Filter, ChevronRight } from 'lucide-react';

interface PaymentsLedgerProps {
  orders: Order[];
  onUpdateOrder: (order: Order) => void;
}

interface FlattenedPayment extends PaymentRecord {
  orderId: string;
  supplier: string;
}

export const PaymentsLedger: React.FC<PaymentsLedgerProps> = ({ orders, onUpdateOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPayment, setEditingPayment] = useState<{ orderId: string, payment: FlattenedPayment } | null>(null);
  const [editForm, setEditForm] = useState<Partial<PaymentRecord>>({});

  // Flatten payments from all orders into a single list
  const allPayments = useMemo(() => {
    const flattened: FlattenedPayment[] = [];
    orders.forEach(order => {
      (order.financials.paymentHistory || []).forEach(pay => {
        flattened.push({
          ...pay,
          orderId: order.id,
          supplier: order.supplier
        });
      });
    });
    return flattened.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [orders]);

  const filteredPayments = allPayments.filter(p => 
    p.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.note?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startEdit = (payment: FlattenedPayment) => {
    setEditingPayment({ orderId: payment.orderId, payment });
    setEditForm({
      amount: payment.amount,
      date: payment.date,
      type: payment.type,
      note: payment.note
    });
  };

  const cancelEdit = () => {
    setEditingPayment(null);
    setEditForm({});
  };

  const handleSave = () => {
    if (!editingPayment) return;

    const parentOrder = orders.find(o => o.id === editingPayment.orderId);
    if (!parentOrder) return;

    const updatedHistory = (parentOrder.financials.paymentHistory || []).map(p => 
      p.id === editingPayment.payment.id ? { ...p, ...editForm } as PaymentRecord : p
    );

    // Recalculate parent order financials
    const totalPaid = updatedHistory.reduce((sum, p) => sum + p.amount, 0);
    const balance = parentOrder.financials.totalAmount - totalPaid;
    
    let status = PaymentStatus.PENDING;
    if (totalPaid >= parentOrder.financials.totalAmount) status = PaymentStatus.PAID;
    else if (totalPaid > 0) status = PaymentStatus.PARTIAL;

    onUpdateOrder({
      ...parentOrder,
      financials: {
        ...parentOrder.financials,
        amountPaid: totalPaid,
        balanceAmount: balance,
        paymentStatus: status,
        paymentHistory: updatedHistory
      }
    });

    cancelEdit();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Global Financial Ledger</h2>
          <p className="text-slate-500 text-sm">Unified registry of all disbursements to project suppliers.</p>
        </div>
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input 
            type="text" 
            placeholder="Filter by Supplier or Order ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/10 outline-none shadow-sm text-sm transition-all"
          />
        </div>
      </div>

      <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
              <tr>
                <th className="px-8 py-5 text-left">Transaction Date</th>
                <th className="px-8 py-5 text-left">Recipient (Supplier)</th>
                <th className="px-8 py-5 text-left">Order Ref</th>
                <th className="px-8 py-5 text-left">Classification</th>
                <th className="px-8 py-5 text-right">Amount ($)</th>
                <th className="px-8 py-5 text-right">Operations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredPayments.map(pay => {
                const isEditing = editingPayment?.payment.id === pay.id;
                return (
                  <tr key={pay.id} className={`hover:bg-indigo-50/30 transition-colors group ${isEditing ? 'bg-indigo-50/50' : ''}`}>
                    <td className="px-8 py-5 whitespace-nowrap">
                      {isEditing ? (
                        <input 
                          type="date" 
                          value={editForm.date} 
                          onChange={e => setEditForm({...editForm, date: e.target.value})}
                          className="p-1.5 border border-indigo-200 rounded-lg text-xs font-bold" 
                        />
                      ) : (
                        <div className="flex items-center gap-2 text-slate-500 font-bold text-xs">
                          <Calendar size={14} className="text-slate-300" />
                          {pay.date}
                        </div>
                      )}
                    </td>
                    <td className="px-8 py-5">
                      <div className="font-black text-slate-800 text-sm uppercase tracking-tight">{pay.supplier}</div>
                    </td>
                    <td className="px-8 py-5 font-mono text-xs text-indigo-600 font-bold">
                      {pay.orderId}
                    </td>
                    <td className="px-8 py-5 whitespace-nowrap">
                      {isEditing ? (
                        <select 
                          value={editForm.type} 
                          onChange={e => setEditForm({...editForm, type: e.target.value as any})}
                          className="p-1.5 border border-indigo-200 rounded-lg text-xs font-bold text-slate-700"
                        >
                          <option value="Advance">Advance</option>
                          <option value="Part Payment">Part Payment</option>
                          <option value="Balance">Balance</option>
                        </select>
                      ) : (
                        <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${
                          pay.type === 'Advance' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                          pay.type === 'Balance' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                          'bg-indigo-50 text-indigo-700 border-indigo-100'
                        }`}>
                          {pay.type}
                        </span>
                      )}
                    </td>
                    <td className="px-8 py-5 text-right font-black text-slate-900 text-sm">
                      {isEditing ? (
                        <input 
                          type="number" 
                          value={editForm.amount} 
                          onChange={e => setEditForm({...editForm, amount: parseFloat(e.target.value) || 0})}
                          className="w-28 p-1.5 border border-indigo-200 rounded-lg text-xs text-right font-black" 
                        />
                      ) : (
                        `$${pay.amount.toLocaleString()}`
                      )}
                    </td>
                    <td className="px-8 py-5 text-right">
                      {isEditing ? (
                        <div className="flex justify-end gap-2">
                          <button onClick={handleSave} className="p-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-all shadow-md">
                            <Check size={14} />
                          </button>
                          <button onClick={cancelEdit} className="p-2 bg-slate-100 text-slate-400 rounded-xl hover:bg-slate-200 transition-all">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => startEdit(pay)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-xl border border-transparent hover:border-slate-100 transition-all"
                            title="Edit Payment"
                          >
                            <Edit2 size={16} />
                          </button>
                          <ChevronRight size={14} className="text-slate-200" />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredPayments.length === 0 && (
            <div className="py-20 text-center">
              <DollarSign size={40} className="mx-auto text-slate-100 mb-4" />
              <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No records found matching criteria.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
